/**
 * Helpers for alert detail: trigger evidence display and deep links to Gaming Data.
 */

import { formatGamingMoney, formatMeterCents } from '../../utils/gamingCurrency'
import {
  formatSydneyDate,
  formatSydneyDateTime,
  formatSydneyDateTimeSec,
  toSydneyDatetimeLocal,
  sydneyWallClockToDate,
} from '../../utils/sydneyDateTime'

/** datetime-local value in Sydney wall clock (AEST/AEDT), not the browser's zone. */
export function toDatetimeLocal(d) {
  return toSydneyDatetimeLocal(d)
}

/**
 * Convert ISO / Date / datetime-local into datetime-local for <input type="datetime-local">.
 * ISO with Z is an absolute instant, shown as Sydney wall clock (daylight saving applied).
 */
export function toDatetimeLocalFromAny(value) {
  if (value == null || value === '') return ''
  const s = String(value).trim()
  if (!s) return ''
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) {
    // Already looks like datetime-local (YYYY-MM-DDTHH:mm)
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return s.slice(0, 16)
    return s
  }
  // Zone-less "YYYY-MM-DDTHH:mm" is already a Sydney wall clock; don't shift it.
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?$/.test(s)) return s.replace(' ', 'T').slice(0, 16)
  return toDatetimeLocal(d)
}

/**
 * Normalize a filter value for gaming API from/to params.
 * Zone-less datetime-local values are Sydney wall clock; convert to ISO UTC so the server
 * (typically UTC) does not mis-parse naive strings as UTC and shift the window.
 */
export function toApiDateTimeParam(value) {
  if (value == null || value === '') return ''
  const s = String(value).trim()
  if (!s) return ''
  if (/Z$/i.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
    const d = new Date(s)
    return Number.isNaN(d.getTime()) ? s : d.toISOString()
  }
  // datetime-local / naive: the value is Sydney wall clock (DST applied), not browser-local
  const d = sydneyWallClockToDate(s) || new Date(s)
  return Number.isNaN(d.getTime()) ? s : d.toISOString()
}

/** Default half-width for gaming / meter context around an alert (rules often correlate over hours). */
export const ALERT_GAMING_CONTEXT_HALF_MS = 6 * 60 * 60 * 1000

/**
 * Time window around anchor for gaming API query params (ISO UTC with Z).
 * @param {string|Date} anchorIso
 * @param {{ halfSpanMs?: number }} [options] — default halfSpanMs = 30 minutes; use ALERT_GAMING_CONTEXT_HALF_MS for gaming APIs
 */
export function alertWindowBounds(anchorIso, options = {}) {
  const halfMs = options.halfSpanMs != null ? options.halfSpanMs : 30 * 60 * 1000
  const d = anchorIso ? new Date(anchorIso) : new Date()
  if (Number.isNaN(d.getTime())) return null
  const from = new Date(d.getTime() - halfMs)
  const to = new Date(d.getTime() + halfMs)
  return { from: from.toISOString(), to: to.toISOString() }
}

/**
 * Prefer play / trigger timestamps from the alert payload over alert first_seen
 * (live-run first_seen is often "today" while the gaming day is earlier).
 *
 * @param {object|null|undefined} alertRow
 * @param {object|null|undefined} detailsParsed — parseDetailsObject(alert.details)
 * @returns {{ anchor: string, source: string } | null}
 */
export function resolveAlertGamingContextAnchor(alertRow, detailsParsed) {
  const tryDate = (raw, source) => {
    if (raw == null || raw === '') return null
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return null
    return { anchor: d.toISOString(), source }
  }

  const pickFromRecord = (rec, label) => {
    if (!rec || typeof rec !== 'object') return null
    const keys = [
      ['StartTime', `${label} start`],
      ['starttime', `${label} start`],
      ['SessionStart', `${label} session start`],
      ['sessionstart', `${label} session start`],
      ['SnapShotTime', `${label} snapshot`],
      ['snapshottime', `${label} snapshot`],
      ['IntervalStart', `${label} interval start`],
      ['intervalstart', `${label} interval start`],
      ['EndTime', `${label} end`],
      ['endtime', `${label} end`],
    ]
    for (const [k, src] of keys) {
      if (rec[k] != null && rec[k] !== '') {
        const hit = tryDate(rec[k], src)
        if (hit) return hit
      }
    }
    const gd = rec.GamingDate ?? rec.gamingdate ?? rec.gaming_date
    if (gd != null && gd !== '') {
      const s = String(gd).slice(0, 10)
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        // Midday local-ish UTC so ±6h still covers the trading day core
        return tryDate(`${s}T12:00:00.000Z`, `${label} gaming date`)
      }
      return tryDate(gd, `${label} gaming date`)
    }
    return null
  }

  const d = detailsParsed && typeof detailsParsed === 'object' ? detailsParsed : {}
  const fromTd = pickFromRecord(d.triggered_data, 'trigger')
  if (fromTd) return fromTd

  if (Array.isArray(d.evidence_items)) {
    for (const item of d.evidence_items) {
      const hit = pickFromRecord(item, 'evidence')
      if (hit) return hit
    }
  }

  // Session PK often embeds YYYYMMDDHHmmss (e.g. 11077-020388-20260708143952)
  const sessionId = String(alertRow?.session_id || alertRow?.sessionId || '').trim()
  const m14 = sessionId.match(/(20\d{12})(?:$|[^0-9])/)
  if (m14) {
    const raw = m14[1]
    const iso = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}T${raw.slice(8, 10)}:${raw.slice(10, 12)}:${raw.slice(12, 14)}.000Z`
    const hit = tryDate(iso, 'session id timestamp')
    if (hit) return hit
  }
  const m8 = sessionId.match(/(20\d{6})(?:$|[^0-9])/)
  if (m8) {
    const raw = m8[1]
    const iso = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}T12:00:00.000Z`
    const hit = tryDate(iso, 'session id date')
    if (hit) return hit
  }

  const fromAlert = tryDate(alertRow?.first_seen || alertRow?.last_updated, 'alert first seen')
  return fromAlert
}

export function parseDetailsObject(details) {
  if (details == null) return {}
  if (typeof details === 'string') {
    try {
      return JSON.parse(details || '{}')
    } catch {
      return {}
    }
  }
  return typeof details === 'object' ? details : {}
}

const SKIP_KEYS = new Set(['recommended_actions', 'execution_id', 'trigger_source', 'cumulative_meter_evidence'])

/** Keys commonly present on meter / EGM snapshot rows evaluated by rules. */
const METER_TRIGGER_KEYS = [
  'SnapshotTime',
  'EgmPK',
  'GamingDate',
  'MeterReadTime',
  'Turnover',
  'CreditWin',
  'CancelCredit',
  'Stroke',
  'Credit',
  'CashlessIn',
  'CashlessOut',
  'CashIn',
  'CashDrop',
  'CoinOut',
  'Bill5',
  'Bill10',
  'Bill20',
  'Bill50',
  'Bill100',
  'TicketsIn',
]

/** Keys commonly present on gaming session rows. */
const SESSION_TRIGGER_KEYS = [
  'SessionPK',
  'MemberID',
  'PlayerID',
  'EgmName',
  'EgmPK',
  'GameVariationPK',
  'GamingDate',
  'StartTime',
  'EndTime',
  'SessionStart',
  'SessionEnd',
  'Turnover',
  'Stroke',
  'CreditWin',
  'ExternalWin',
  'Rewards',
  'PointsToCr',
  'CashIn',
  'CashDrop',
  'CashlessIn',
  'CashlessOut',
  'Venue',
  'MeterValid',
]

/** Keys on combined / interval attribution rows. */
const COMBINED_TRIGGER_KEYS = [
  'IntervalStart',
  'IntervalEnd',
  'SessionPK',
  'MemberID',
  'EgmPK',
  'Venue',
  'GameVariationPK',
  'overlap_fraction',
  'overlap_ms',
  'Turnover_alloc',
  'CreditWin_alloc',
  'CancelCredit_alloc',
  'Stroke_alloc',
  'Credit_alloc',
  'CashlessIn_alloc',
  'CashlessOut_alloc',
  'CashIn_alloc',
  'CashDrop_alloc',
  'CoinOut_alloc',
  'Bill5_alloc',
  'Bill10_alloc',
  'Bill20_alloc',
  'Bill50_alloc',
  'Bill100_alloc',
  'TicketsIn_alloc',
  'SessionStart',
  'SessionEnd',
]

function getKeyCI(obj, name) {
  if (!obj || typeof obj !== 'object') return { k: null, v: undefined }
  if (Object.prototype.hasOwnProperty.call(obj, name)) return { k: name, v: obj[name] }
  const lower = name.toLowerCase()
  const found = Object.keys(obj).find((x) => x.toLowerCase() === lower)
  if (found) return { k: found, v: obj[found] }
  return { k: null, v: undefined }
}

const TRIGGER_TIMESTAMP_KEY_RE =
  /^(starttime|endtime|sessionstart|sessionend|sessionstarttime|sessionendtime|intervalstart|intervalend|snapshottime|meterreadtime|transactiontime|transactiondatetime|txntime|timestamp|created_at|createdat|occurred_at|occurredat|first_seen|last_updated|entry_datetime|entrydatetime)$/i
const TRIGGER_DATE_KEY_RE = /^(gamingdate|transactiondate)$/i

/**
 * Stored instants are UTC. A string with no zone ("2026-10-12 03:20:00" or "2026-10-12T03:20:00")
 * would be read as the browser's local time by `new Date`, so mark it UTC before formatting.
 */
function utcInstantForDisplay(raw) {
  const s = String(raw).trim()
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) return s
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s)) return `${s.replace(' ', 'T')}Z`
  return s
}

function isIsoTimestampString(v) {
  if (v == null || v === '') return false
  if (v instanceof Date) return !Number.isNaN(v.getTime())
  const s = String(v).trim()
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return false
  const d = new Date(s)
  return !Number.isNaN(d.getTime())
}

function formatTriggerScalar(v, key = null) {
  if (v == null) return '—'
  if (Array.isArray(v)) return v.length <= 3 ? JSON.stringify(v) : `[${v.length} items]`
  if (typeof v === 'object') return JSON.stringify(v)

  const keyLeaf = key != null ? String(key).split('.').pop().toLowerCase() : ''
  const raw = String(v).trim()

  if (keyLeaf && TRIGGER_DATE_KEY_RE.test(keyLeaf) && isIsoTimestampString(raw)) {
    const datePart = raw.slice(0, 10)
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return formatSydneyDate(datePart)
  }
  if (keyLeaf && TRIGGER_TIMESTAMP_KEY_RE.test(keyLeaf) && isIsoTimestampString(raw)) {
    return formatSydneyDateTime(utcInstantForDisplay(raw))
  }
  // Date-times in "other" fields (e.g. sessionend without a known key list match)
  if (isIsoTimestampString(raw) && /[T ]\d{2}:\d{2}/.test(raw)) {
    return formatSydneyDateTime(utcInstantForDisplay(raw))
  }

  return raw
}

/**
 * Format money on alert trigger evidence.
 * - `*_alloc` money fields are meter cents
 * - CashIn/CashDrop/Cashless* with cash_fields_unit=AUD are dollars (fixed dry-run)
 * - Legacy session CashIn without that flag was stored as meter cents
 */
export function formatTriggerFieldValue(key, value, triggeredData = null) {
  if (value == null || value === '') return '—'
  const lk = String(key || '').toLowerCase()
  const n = Number(value)
  if (!Number.isFinite(n)) return formatTriggerScalar(value, key)

  const isAllocMoney = /_alloc$/.test(lk) && /cash|turnover|creditwin|coin|ticket/i.test(lk)
  const isSessionCash = /^(cashin|cashdrop|cashlessin|cashlessout)$/.test(lk)
  if (isAllocMoney) return formatMeterCents(n)

  if (isSessionCash) {
    const unit = String(triggeredData?.cash_fields_unit || triggeredData?._cash_fields_unit || '').toUpperCase()
    if (unit === 'AUD') return formatGamingMoney(n)
    return formatMeterCents(n)
  }

  if (/^(turnover|creditwin)$/.test(lk)) return formatGamingMoney(n)
  return formatTriggerScalar(value, key)
}

/**
 * Human-readable provenance for the record in details.triggered_data.
 * @param {object|null|undefined} triggeredData — details.triggered_data
 * @param {string|null|undefined} ruleType — alerts.rule_type
 */
export function getTriggerSourceInfo(triggeredData, ruleType) {
  const td = triggeredData && typeof triggeredData === 'object' && !Array.isArray(triggeredData) ? triggeredData : null
  const has = (name) => {
    if (!td) return false
    return getKeyCI(td, name).k != null
  }

  let kind = 'unknown'
  let label = 'Rule evaluation record'
  let storage = ''

  if (has('IntervalStart') || has('Turnover_alloc') || has('overlap_fraction')) {
    kind = 'combined'
    label = 'Combined interval attribution (meter × session)'
    storage =
      'Usually the interval attribution row supplied to the rule (same family as `gaming_session_interval_attribution` / Combined data in Gaming Data).'
  } else if (has('SnapshotTime')) {
    kind = 'meter'
    label = 'EGM meter reading snapshot'
    storage = ''
  } else if (has('SessionPK') && (has('StartTime') || has('EndTime'))) {
    kind = 'session'
    label = 'Gaming session record'
    storage = 'Usually a row from `gaming_sessions` (start/end play on an EGM).'
  } else if (has('TransactionPK')) {
    kind = 'transaction'
    label = 'Gaming transaction record'
    storage = 'Usually a row from gaming or cash transaction feeds (e.g. `gaming_transactions`).'
  }

  const rt = ruleType ? String(ruleType).toLowerCase() : ''
  if (kind === 'unknown' && rt.includes('session')) {
    label = 'Session-style rule evaluation record'
  }
  if (kind === 'unknown' && rt.includes('transaction')) {
    label = 'Transaction-style rule evaluation record'
  }

  return { kind, label, storage, ruleType: ruleType || null }
}

function inferTriggeredShape(td) {
  const has = (name) => getKeyCI(td, name).k != null
  if (has('IntervalStart') || has('Turnover_alloc') || has('overlap_fraction')) return 'combined'
  if (has('SnapshotTime')) return 'meter'
  if (has('SessionPK') && (has('StartTime') || has('EndTime'))) return 'session'
  if (has('TransactionPK')) return 'transaction'
  return 'unknown'
}

/**
 * Split triggered_data into meter / session / combined buckets for display (each key once).
 * Shape is inferred so shared keys (e.g. EgmPK) land in the correct group.
 */
export function groupTriggeredDataFields(triggeredData) {
  const empty = { meter: [], session: [], combined: [], other: [], shape: 'none' }
  const td = triggeredData && typeof triggeredData === 'object' && !Array.isArray(triggeredData) ? triggeredData : null
  if (!td) return empty

  const shape = inferTriggeredShape(td)
  const used = new Set()

  const take = (keyList, bucket) => {
    for (const name of keyList) {
      const { k, v } = getKeyCI(td, name)
      if (k == null || v === undefined) continue
      const lk = k.toLowerCase()
      if (used.has(lk)) continue
      used.add(lk)
      bucket.push({ key: k, value: formatTriggerFieldValue(k, v, td) })
    }
  }

  const combined = []
  const meter = []
  const session = []

  if (shape === 'combined') {
    take(COMBINED_TRIGGER_KEYS, combined)
  } else if (shape === 'meter') {
    take(METER_TRIGGER_KEYS, meter)
  } else if (shape === 'session') {
    take(SESSION_TRIGGER_KEYS, session)
  } else if (shape === 'transaction') {
    take(SESSION_TRIGGER_KEYS, session)
    take(METER_TRIGGER_KEYS, meter)
  } else {
    // unknown shape: try to surface likely columns without mis-labeling
    take(METER_TRIGGER_KEYS, meter)
    take(SESSION_TRIGGER_KEYS, session)
    take(COMBINED_TRIGGER_KEYS, combined)
  }

  const other = []
  for (const k of Object.keys(td)) {
    const lk = k.toLowerCase()
    if (used.has(lk)) continue
    other.push({ key: k, value: formatTriggerFieldValue(k, td[k], td) })
  }
  other.sort((a, b) => a.key.localeCompare(b.key))

  return { meter, session, combined, other, shape }
}

/**
 * Split combined/attribution trigger rows into allocation vs session-linkage for display.
 */
export function splitCombinedTriggerRows(combinedRows) {
  const allocation = []
  const gaming = []
  if (!Array.isArray(combinedRows)) return { allocation, gaming }
  const reAlloc =
    /^(IntervalStart|IntervalEnd|overlap_ms|overlap_fraction|Turnover_alloc|CreditWin_alloc|CancelCredit_alloc|Stroke_alloc|Credit_alloc|CashlessIn_alloc|CashlessOut_alloc|CashIn_alloc|CashDrop_alloc|CoinOut_alloc|Bill5_alloc|Bill10_alloc|Bill20_alloc|Bill50_alloc|Bill100_alloc|TicketsIn_alloc|interval_reset|reconciliation_tag)$/i
  const reGaming = /^(SessionPK|MemberID|SessionStart|SessionEnd|Venue|GameVariationPK|EgmPK)$/i
  const rest = []
  for (const row of combinedRows) {
    const k = row.key || ''
    if (reAlloc.test(k)) allocation.push(row)
    else if (reGaming.test(k)) gaming.push(row)
    else rest.push(row)
  }
  if (rest.length) {
    allocation.push(...rest)
  }
  return { allocation, gaming }
}

/** Single table: combined + meter + session + other buckets from `groupTriggeredDataFields`. */
export function groupedFieldsToRows(fieldGroups) {
  if (!fieldGroups || fieldGroups.shape === 'none') return []
  const { combined = [], meter = [], session = [], other = [] } = fieldGroups
  return [...combined, ...meter, ...session, ...other]
}

/**
 * Extra meter/feed rows the engine used in a cumulative / structuring window (beyond the primary snapshot).
 * Prefer `details.cumulative_meter_evidence` (array of feed objects, same shape as `triggered_data`).
 */
export function extractCumulativeMeterSnapshots(detailsParsed) {
  const d = detailsParsed || {}
  const out = []
  const push = (arr) => {
    for (const row of arr) {
      if (row && typeof row === 'object' && !Array.isArray(row)) {
        out.push({ index: out.length + 1, data: row })
      }
    }
  }
  if (Array.isArray(d.cumulative_meter_evidence) && d.cumulative_meter_evidence.length) {
    push(d.cumulative_meter_evidence)
    return out
  }
  if (Array.isArray(d.evidence_items) && d.evidence_items.length > 1) {
    const meterish = d.evidence_items.filter(
      (x) =>
        x &&
        typeof x === 'object' &&
        !Array.isArray(x) &&
        (x.SnapshotTime != null ||
          x.SnapShotTime != null ||
          x.snapshot_time != null ||
          x.EgmPK != null ||
          x.EgmHouseNo != null)
    )
    if (meterish.length > 1) push(meterish)
  }
  if (Array.isArray(d.triggered_data) && d.triggered_data.length > 1) {
    push(d.triggered_data)
  }
  return out
}

/**
 * Human-readable column titles aligned with Gaming Data (meter / sessions / combined tabs).
 * Keys are matched case-insensitively on the last path segment (e.g. triggered_data.SnapshotTime).
 */
const TRIGGER_FIELD_LABELS = {
  snapshottime: 'Snapshot time (meter)',
  meterreadtime: 'Meter read time',
  gamingdate: 'Gaming date',
  egmpk: 'EGM PK',
  egmhouseno: 'EGM house no.',
  egmname: 'EGM name',
  sessionpk: 'Session PK',
  transactionpk: 'Transaction PK',
  memberid: 'Player ID',
  venue: 'Venue',
  gamevariationpk: 'Game variation PK',
  starttime: 'Session start',
  endtime: 'Session end',
  sessionstart: 'Session start (interval)',
  sessionend: 'Session end (interval)',
  intervalstart: 'Interval start',
  intervalend: 'Interval end',
  overlap_ms: 'Overlap (ms)',
  overlap_fraction: 'Overlap fraction',
  turnover: 'Turnover',
  creditwin: 'Credit win',
  cancelcredit: 'Cancel credit',
  stroke: 'Stroke',
  credit: 'Credit',
  cashlessin: 'Cashless in',
  cashlessout: 'Cashless out',
  cashin: 'Cash in',
  cashdrop: 'Cash drop',
  coinout: 'Coin out',
  bill5: '$5 bills',
  bill10: '$10 bills',
  bill20: '$20 bills',
  bill50: '$50 bills',
  bill100: '$100 bills',
  ticketsin: 'Tickets in',
  externalwin: 'External win',
  rewards: 'Rewards',
  pointstocr: 'Points to credit',
  metervalid: 'Meter valid',
  turnover_alloc: 'Turnover (allocated)',
  creditwin_alloc: 'Credit win (allocated)',
  cancelcredit_alloc: 'Cancel credit (allocated)',
  stroke_alloc: 'Stroke (allocated)',
  credit_alloc: 'Credit (allocated)',
  cashlessin_alloc: 'Cashless in (allocated)',
  cashlessout_alloc: 'Cashless out (allocated)',
  cashin_alloc: 'Cash in (allocated)',
  cashdrop_alloc: 'Cash drop (allocated)',
  coinout_alloc: 'Coin out (allocated)',
  bill5_alloc: '$5 bills (allocated)',
  bill10_alloc: '$10 bills (allocated)',
  bill20_alloc: '$20 bills (allocated)',
  bill50_alloc: '$50 bills (allocated)',
  bill100_alloc: '$100 bills (allocated)',
  ticketsin_alloc: 'Tickets in (allocated)',
  interval_reset: 'Interval reset',
  reconciliation_tag: 'Reconciliation tag',
  execution_id: 'Execution ID',
  recommended_actions: 'Recommended actions',
}

/** Tooltip descriptions for gaming metrics to help compliance officers understand field meanings. */
export const TRIGGER_FIELD_TOOLTIPS = {
  snapshottime: 'The exact date and time when this meter reading was captured from the EGM',
  meterreadtime: 'When the meter was physically read or polled',
  gamingdate: 'The gaming day (may differ from calendar date depending on venue cutoff time)',
  egmpk: 'Unique identifier for the Electronic Gaming Machine (poker machine)',
  egmhouseno: 'Venue-specific house number for the gaming machine',
  egmname: 'Descriptive name or location identifier for the machine',
  sessionpk: 'Unique identifier for this gaming session (period of continuous play)',
  transactionpk: 'Unique identifier for this transaction record',
  memberid: 'Patron loyalty card or member identification number',
  venue: 'Venue code or name where the gaming activity occurred',
  gamevariationpk: 'Specific game or variation being played on the machine',
  starttime: 'When the gaming session began',
  endtime: 'When the gaming session ended',
  sessionstart: 'Session start time within this meter interval',
  sessionend: 'Session end time within this meter interval',
  intervalstart: 'Beginning of the meter reading interval period',
  intervalend: 'End of the meter reading interval period',
  overlap_ms: 'Duration in milliseconds that the session overlapped with the meter interval',
  overlap_fraction: 'Percentage of the interval that overlaps with the session (used for allocation calculations)',
  turnover: 'Total amount wagered/bet by the patron. Key indicator of genuine play activity. High turnover with consistent cash flow suggests legitimate gambling.',
  creditwin: 'Total amount won by the patron and credited to their machine balance. Compare with Cash In to assess gambling outcomes.',
  cancelcredit: 'Credits cancelled or voided during play',
  stroke: 'Number of individual game plays/spins executed. Critical metric: low stroke count with high cash flow may indicate money laundering rather than gambling.',
  credit: 'Current remaining credit balance on the machine at this snapshot',
  cashlessin: 'Money deposited via cashless methods (ticket-in, electronic transfer, loyalty points)',
  cashlessout: 'Money withdrawn via cashless methods (ticket-out, electronic transfer)',
  cashin: 'Physical cash inserted into the machine. Multiple high-value cash-ins with minimal play may indicate structuring or laundering.',
  cashdrop: 'Cash that has dropped into the machine collection box (permanently removed from play)',
  coinout: 'Coins dispensed by the machine to the patron',
  bill5: 'Count of $5 bills inserted',
  bill10: 'Count of $10 bills inserted',
  bill20: 'Count of $20 bills inserted',
  bill50: 'Count of $50 bills inserted',
  bill100: 'Count of $100 bills inserted. Multiple high-denomination bills may warrant attention in AML context.',
  ticketsin: 'Tickets inserted into the machine (TITO - Ticket In Ticket Out)',
  externalwin: 'Wins triggered externally (e.g., jackpot, bonus payout)',
  rewards: 'Promotional rewards or bonuses applied',
  pointstocr: 'Loyalty points converted to credits',
  metervalid: 'Indicates whether the meter reading passed validation checks',
  turnover_alloc: 'Turnover amount allocated to this specific interval based on session overlap',
  creditwin_alloc: 'Credit win amount allocated to this interval based on overlap fraction',
  cancelcredit_alloc: 'Cancel credit allocated to this interval',
  stroke_alloc: 'Number of strokes allocated to this interval',
  credit_alloc: 'Credit amount allocated to this interval',
  cashlessin_alloc: 'Cashless in amount allocated to this interval',
  cashlessout_alloc: 'Cashless out amount allocated to this interval',
  cashin_alloc: 'Cash in amount allocated to this interval',
  cashdrop_alloc: 'Cash drop amount allocated to this interval',
  coinout_alloc: 'Coin out amount allocated to this interval',
  bill5_alloc: '$5 bills allocated to this interval',
  bill10_alloc: '$10 bills allocated to this interval',
  bill20_alloc: '$20 bills allocated to this interval',
  bill50_alloc: '$50 bills allocated to this interval',
  bill100_alloc: '$100 bills allocated to this interval',
  ticketsin_alloc: 'Tickets in allocated to this interval',
  interval_reset: 'Indicates if the meter interval was reset',
  reconciliation_tag: 'Tag used for reconciliation and data integrity checks',
  execution_id: 'Rules engine execution identifier for this evaluation',
  recommended_actions: 'Automated recommendations generated by the rules engine',
}

/** Get tooltip text for a field key (case-insensitive lookup). */
export function getFieldTooltip(key) {
  if (key == null || key === '') return null
  const s = String(key)
  const last = s.includes('.') ? s.slice(s.lastIndexOf('.') + 1) : s
  const lk = last.toLowerCase()
  return TRIGGER_FIELD_TOOLTIPS[lk] || null
}

export function displayTriggerFieldKey(key) {
  if (key == null || key === '') return '—'
  const s = String(key)
  const last = s.includes('.') ? s.slice(s.lastIndexOf('.') + 1) : s
  const lk = last.toLowerCase()
  if (TRIGGER_FIELD_LABELS[lk]) return TRIGGER_FIELD_LABELS[lk]
  return last.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim()
}

function sortComparisonKeys(allKeys) {
  const preferred = [...COMBINED_TRIGGER_KEYS, ...METER_TRIGGER_KEYS, ...SESSION_TRIGGER_KEYS]
  const ordered = []
  const usedLower = new Set()
  for (const p of preferred) {
    const hit = allKeys.find((k) => k.toLowerCase() === p.toLowerCase())
    if (hit != null && !usedLower.has(hit.toLowerCase())) {
      usedLower.add(hit.toLowerCase())
      ordered.push(hit)
    }
  }
  for (const k of [...allKeys].sort((a, b) => a.localeCompare(b))) {
    if (!usedLower.has(k.toLowerCase())) {
      usedLower.add(k.toLowerCase())
      ordered.push(k)
    }
  }
  return ordered
}

/**
 * Comparison table: each column is one meter/feed reading; rows are field names (union across readings).
 * @param {Array<{ data: object, title?: string }>} columnSnapshots — optional `title` for header (e.g. "At trigger", "Reading 2")
 * @returns {{ headers: string[], rowLabels: string[], rows: string[][] } | null}
 */
export function buildCumulativeMeterComparisonTable(columnSnapshots) {
  if (!Array.isArray(columnSnapshots) || columnSnapshots.length === 0) return null
  const valid = columnSnapshots.filter((c) => c?.data && typeof c.data === 'object' && !Array.isArray(c.data))
  if (valid.length === 0) return null

  const keySet = new Set()
  for (const { data } of valid) {
    Object.keys(data).forEach((k) => keySet.add(k))
  }
  const keys = sortComparisonKeys([...keySet])
  const headers = valid.map((c, i) => c.title || `Reading ${i + 1}`)
  const rowLabels = keys.map((k) => displayTriggerFieldKey(k))
  const rows = keys.map((key) => valid.map(({ data }) => formatTriggerScalar(getKeyCI(data, key).v)))
  return { headers, rowLabels, rows }
}

/**
 * When `details.triggered_data` is empty, surface alert row columns using the same vocabulary
 * as Gaming Data filters (venue, member, session, EGM).
 */
export function getAlertRowLinkageRows(alertRow) {
  if (!alertRow || typeof alertRow !== 'object') return []
  const rows = []
  const push = (label, val) => {
    if (val == null || val === '') return
    rows.push({ key: label, value: String(val) })
  }
  push('Venue', alertRow.venue_name || alertRow.venue_code_raw || alertRow.venue_code)
  push('Member #', alertRow.membership_number || alertRow.member_id)
  push('Session PK (linkage)', alertRow.session_id)
  push('Transaction PK (linkage)', alertRow.transaction_id)
  push('EGM house no.', alertRow.egm_house_no)
  push('EGM name', alertRow.egm_name)
  return rows
}

/** execution_id / recommended_actions on details (not in triggered_data flatten). */
export function getDetailMetaRows(detailsParsed) {
  const rows = []
  const d = detailsParsed || {}
  if (d.execution_id != null && d.execution_id !== '') {
    rows.push({ key: 'execution_id', value: String(d.execution_id) })
  }
  if (d.recommended_actions != null) {
    rows.push({
      key: 'recommended_actions',
      value: typeof d.recommended_actions === 'object' ? JSON.stringify(d.recommended_actions) : String(d.recommended_actions),
    })
  }
  return rows
}

/** Flatten nested objects for a simple label / value list (max depth). */
export function flattenTriggerEvidence(obj, prefix = '', depth = 0) {
  const rows = []
  if (obj == null || typeof obj !== 'object' || depth > 5) return rows
  for (const [k, v] of Object.entries(obj)) {
    if (SKIP_KEYS.has(k)) continue
    const key = prefix ? `${prefix}.${k}` : k
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      rows.push(...flattenTriggerEvidence(v, key, depth + 1))
      continue
    }
    let display
    if (v == null) display = '—'
    else if (Array.isArray(v)) display = v.length <= 3 ? JSON.stringify(v) : `[${v.length} items]`
    else if (typeof v === 'object') display = JSON.stringify(v)
    else display = String(v)
    rows.push({ key, value: display })
  }
  return rows
}

/** Prefer engine snapshot: triggered_data, then top-level record fields. */
export function getTriggerEvidenceRows(detailsParsed) {
  const d = detailsParsed || {}
  const td = d.triggered_data
  if (td && typeof td === 'object' && !Array.isArray(td)) {
    const rows = flattenTriggerEvidence(td, 'triggered_data')
    if (rows.length) return rows
  }
  return flattenTriggerEvidence(d)
}

function parseAlertTimeMs(iso) {
  if (iso == null || iso === '') return NaN
  const s = String(iso).trim()
  const normalized = s.includes('T') ? s : s.replace(' ', 'T')
  return new Date(normalized).getTime()
}

/**
 * When GET /api/gaming/egms/readings returns no rows, build table rows from alert.details
 * (triggered_data + cumulative_meter_evidence) so the modal still shows metering context.
 */
export function gamingModalMeterFallbackRows(detailsParsed, fromStr, toStr) {
  const fromMs = parseAlertTimeMs(fromStr)
  const toMs = parseAlertTimeMs(toStr)
  const windowOk = !Number.isNaN(fromMs) && !Number.isNaN(toMs) && toMs >= fromMs

  const inWindow = (iso) => {
    if (!windowOk) return true
    const t = parseAlertTimeMs(iso)
    if (Number.isNaN(t)) return false
    return t >= fromMs && t <= toMs
  }

  const mapMeterRow = (data) => ({
    SnapshotTime: data.SnapshotTime ?? data.SnapShotTime ?? data.snapshot_time ?? null,
    MemberID: data.MemberID ?? data.memberid ?? data.member_id ?? null,
    EgmPK: data.EgmPK ?? data.EgmHouseNo ?? data.egmpk ?? null,
    GamingDate: data.GamingDate ?? data.gamingdate ?? null,
    Turnover: data.Turnover ?? data.turnover,
    CreditWin: data.CreditWin ?? data.creditwin,
    Stroke: data.Stroke ?? data.stroke,
    CashIn: data.CashIn ?? data.cashin,
    CashlessIn: data.CashlessIn ?? data.cashlessin,
  })

  const out = []
  const seen = new Set()
  const pushUnique = (row) => {
    const k = `${row.EgmPK ?? ''}|${row.SnapshotTime ?? ''}`
    if (seen.has(k)) return
    seen.add(k)
    out.push(row)
  }

  const td = detailsParsed?.triggered_data
  if (td && typeof td === 'object' && !Array.isArray(td)) {
    const st = td.SnapshotTime ?? td.SnapShotTime ?? td.snapshot_time
    if (st && inWindow(st)) pushUnique(mapMeterRow(td))
  }

  for (const { data } of extractCumulativeMeterSnapshots(detailsParsed)) {
    if (!data || typeof data !== 'object') continue
    const st = data.SnapshotTime ?? data.SnapShotTime ?? data.snapshot_time
    if (st && inWindow(st)) pushUnique(mapMeterRow(data))
  }

  let hint = null
  if (out.length > 0) {
    hint =
      'No rows from the tenant gaming DB for this ±6h window. Showing snapshots from the alert that fall inside the window.'
  } else {
    if (td && typeof td === 'object' && !Array.isArray(td)) {
      const st = td.SnapshotTime ?? td.SnapShotTime ?? td.snapshot_time
      if (st) {
        pushUnique(mapMeterRow(td))
        hint =
          'No tenant DB rows in ±6h. Showing the primary trigger snapshot from the alert payload.'
      }
    }
    if (out.length === 0) {
      for (const { data } of extractCumulativeMeterSnapshots(detailsParsed)) {
        if (data && typeof data === 'object') pushUnique(mapMeterRow(data))
      }
      if (out.length) {
        hint =
          'No tenant DB rows in ±6h. Showing cumulative meter snapshots stored on the alert (evaluation window; may extend beyond ±6h).'
      }
    }
  }

  return { rows: out, hint }
}

/**
 * Member ID for meter modal context (meters are EGM-level; member comes from the alert / trigger).
 */
export function resolveAlertMemberIdForMeterModal(alertRow, detailsParsed) {
  const fromAlert = String(alertRow?.member_id ?? alertRow?.memberId ?? '').trim()
  if (fromAlert) return fromAlert
  const td = detailsParsed?.triggered_data
  if (td && typeof td === 'object' && !Array.isArray(td)) {
    for (const k of ['MemberID', 'memberid', 'MemberId', 'member_id', 'PlayerID', 'playerid']) {
      const v = td[k]
      if (v != null && String(v).trim() !== '' && String(v).trim() !== '0') return String(v).trim()
    }
  }
  return ''
}

/**
 * Pick the meter SnapshotTime that best matches the alert trigger so the modal can highlight it.
 * Prefers exact SnapshotTime, then IntervalEnd (combined), then session EndTime bracket, then closest to anchor.
 *
 * @returns {{ index: number, matchKind: string, targetIso: string|null, badge: string } | null}
 */
export function findTriggerMeterRowIndex(rows, detailsParsed, alertRow) {
  if (!Array.isArray(rows) || rows.length === 0) return null

  const td =
    detailsParsed?.triggered_data && typeof detailsParsed.triggered_data === 'object'
      ? detailsParsed.triggered_data
      : null

  const rowMs = (r) => parseAlertTimeMs(r?.SnapshotTime ?? r?.SnapShotTime ?? r?.snapshottime)
  const exactIndex = (targetMs) => {
    if (Number.isNaN(targetMs)) return -1
    return rows.findIndex((r) => {
      const t = rowMs(r)
      return !Number.isNaN(t) && Math.abs(t - targetMs) < 2000
    })
  }

  const snapRaw = td?.SnapshotTime ?? td?.SnapShotTime ?? td?.snapshottime
  const snapMs = parseAlertTimeMs(snapRaw)
  if (!Number.isNaN(snapMs)) {
    const i = exactIndex(snapMs)
    if (i >= 0) {
      return {
        index: i,
        matchKind: 'exact snapshot',
        targetIso: new Date(snapMs).toISOString(),
        badge: 'Trigger',
      }
    }
  }

  const intervalEndMs = parseAlertTimeMs(td?.IntervalEnd ?? td?.intervalend)
  if (!Number.isNaN(intervalEndMs)) {
    const i = exactIndex(intervalEndMs)
    if (i >= 0) {
      return {
        index: i,
        matchKind: 'interval end',
        targetIso: new Date(intervalEndMs).toISOString(),
        badge: 'Trigger',
      }
    }
  }

  const intervalStartMs = parseAlertTimeMs(td?.IntervalStart ?? td?.intervalstart)
  if (!Number.isNaN(intervalStartMs) && !Number.isNaN(intervalEndMs) && intervalEndMs >= intervalStartMs) {
    let best = -1
    for (let i = 0; i < rows.length; i++) {
      const t = rowMs(rows[i])
      if (Number.isNaN(t)) continue
      if (t >= intervalStartMs && t <= intervalEndMs) best = i
    }
    if (best >= 0) {
      return {
        index: best,
        matchKind: 'inside interval',
        targetIso: new Date(intervalEndMs).toISOString(),
        badge: 'Trigger',
      }
    }
  }

  // Session-shaped alerts: highlight the meter snap that brackets the sit (usually first snap at/after EndTime).
  const sessionStartMs = parseAlertTimeMs(td?.StartTime ?? td?.starttime ?? td?.SessionStart)
  const sessionEndMs = parseAlertTimeMs(td?.EndTime ?? td?.endtime ?? td?.SessionEnd)
  if (!Number.isNaN(sessionStartMs) || !Number.isNaN(sessionEndMs)) {
    const endMs = !Number.isNaN(sessionEndMs) ? sessionEndMs : sessionStartMs
    const startMs = !Number.isNaN(sessionStartMs) ? sessionStartMs : endMs
    // Prefer first snapshot at or after session end (captures the interval containing the sit).
    let afterEnd = -1
    let afterEndDist = Infinity
    let inside = -1
    for (let i = 0; i < rows.length; i++) {
      const t = rowMs(rows[i])
      if (Number.isNaN(t)) continue
      if (t >= startMs && t <= endMs) inside = i
      if (t >= endMs) {
        const dist = t - endMs
        if (dist < afterEndDist) {
          afterEndDist = dist
          afterEnd = i
        }
      }
    }
    if (afterEnd >= 0 && afterEndDist <= 20 * 60 * 1000) {
      return {
        index: afterEnd,
        matchKind: 'nearest meter at/after session end',
        targetIso: new Date(endMs).toISOString(),
        badge: 'Session',
      }
    }
    if (inside >= 0) {
      return {
        index: inside,
        matchKind: 'meter during session',
        targetIso: new Date(endMs).toISOString(),
        badge: 'Session',
      }
    }
    // Closest to session midpoint / end
    let bestIdx = -1
    let bestDist = Infinity
    for (let i = 0; i < rows.length; i++) {
      const t = rowMs(rows[i])
      if (Number.isNaN(t)) continue
      const dist = Math.abs(t - endMs)
      if (dist < bestDist) {
        bestDist = dist
        bestIdx = i
      }
    }
    if (bestIdx >= 0 && bestDist <= 2 * 60 * 60 * 1000) {
      return {
        index: bestIdx,
        matchKind: 'nearest meter to session',
        targetIso: new Date(endMs).toISOString(),
        badge: 'Session',
      }
    }
  }

  const resolved = resolveAlertGamingContextAnchor(alertRow, detailsParsed)
  const anchorMs = parseAlertTimeMs(resolved?.anchor)
  if (Number.isNaN(anchorMs)) return null

  let bestIdx = -1
  let bestDist = Infinity
  for (let i = 0; i < rows.length; i++) {
    const t = rowMs(rows[i])
    if (Number.isNaN(t)) continue
    const dist = Math.abs(t - anchorMs)
    if (dist < bestDist) {
      bestDist = dist
      bestIdx = i
    }
  }
  if (bestIdx < 0) return null
  if (bestDist > 2 * 60 * 60 * 1000) return null
  return {
    index: bestIdx,
    matchKind: resolved?.source ? `closest to ${resolved.source}` : 'closest to trigger',
    targetIso: new Date(anchorMs).toISOString(),
    badge: 'Near',
  }
}

/**
 * Values the rule actually evaluated — for meter-modal banner (often session cash, not EGM counters).
 */
export function getMeterModalEvaluatedContext(detailsParsed, alertRow) {
  const td =
    detailsParsed?.triggered_data && typeof detailsParsed.triggered_data === 'object' && !Array.isArray(detailsParsed.triggered_data)
      ? detailsParsed.triggered_data
      : null
  if (!td) return null

  const shape = inferTriggeredShape(td)
  const pick = (...names) => {
    for (const n of names) {
      const { v } = getKeyCI(td, n)
      if (v != null && v !== '') return v
    }
    return null
  }

  const start = pick('StartTime', 'SessionStart', 'starttime')
  const end = pick('EndTime', 'SessionEnd', 'endtime')
  const sessionPk = pick('SessionPK', 'sessionpk')
  const cashIn = pick('CashIn', 'CashIn_alloc', 'cashin')
  const cashDrop = pick('CashDrop', 'CashDrop_alloc', 'cashdrop')
  const cashlessIn = pick('CashlessIn', 'CashlessIn_alloc', 'cashlessin')
  const turnover = pick('Turnover', 'Turnover_alloc', 'turnover')
  const stroke = pick('Stroke', 'Stroke_alloc', 'stroke')

  const moneyBits = [
    cashIn != null ? `CashIn ${formatTriggerFieldValue('CashIn', cashIn, td)}` : null,
    cashDrop != null && String(cashDrop) !== String(cashIn)
      ? `CashDrop ${formatTriggerFieldValue('CashDrop', cashDrop, td)}`
      : null,
    cashlessIn != null ? `CashlessIn ${formatTriggerFieldValue('CashlessIn', cashlessIn, td)}` : null,
    turnover != null ? `Turnover ${formatTriggerFieldValue('Turnover', turnover, td)}` : null,
    stroke != null ? `Stroke ${stroke}` : null,
  ].filter(Boolean)

  let title = 'Rule evaluation record'
  let note = ''
  if (shape === 'session') {
    title = 'Alert evaluated a gaming session (not a single meter snap)'
    note =
      'CashIn / CashDrop above are from the session (or combined sit). Meter CashIn columns below are EGM cumulative counters and often stay 0 / not comparable.'
  } else if (shape === 'combined') {
    title = 'Alert evaluated a combined meter×session interval'
    note = 'Allocated cash figures above may differ from raw EGM meter counters in the table.'
  } else if (shape === 'meter') {
    title = 'Alert evaluated an EGM meter snapshot'
    note = 'Highlighted row should match the evaluated SnapshotTime when present in this window.'
  }

  return {
    shape,
    title,
    note,
    sessionPk: sessionPk != null ? String(sessionPk) : null,
    start: start != null ? String(start) : null,
    end: end != null ? String(end) : null,
    moneyLine: moneyBits.join(' · '),
    memberId: resolveAlertMemberIdForMeterModal(alertRow, detailsParsed),
  }
}

/** Resolve SessionPK from alert row / triggered_data for highlighting combined / sessions modals. */
export function resolveAlertSessionPk(alertRow, detailsParsed) {
  const fromAlert = String(alertRow?.session_id ?? alertRow?.sessionId ?? '').trim()
  if (fromAlert) return fromAlert
  const td = detailsParsed?.triggered_data
  if (td && typeof td === 'object' && !Array.isArray(td)) {
    const spk = td.SessionPK ?? td.sessionpk
    if (spk != null && String(spk).trim()) return String(spk).trim()
  }
  return ''
}

/**
 * When combined API returns no rows, build one session-shaped combined row from triggered_data.
 */
export function gamingModalCombinedFallbackRows(detailsParsed, alertRow) {
  const td =
    detailsParsed?.triggered_data && typeof detailsParsed.triggered_data === 'object' && !Array.isArray(detailsParsed.triggered_data)
      ? detailsParsed.triggered_data
      : null
  if (!td) return { rows: [], hint: null }

  const spk = td.SessionPK ?? td.sessionpk ?? alertRow?.session_id
  if (!spk) return { rows: [], hint: null }

  const cashIn = Number(td.CashIn_alloc ?? td.cashin_alloc ?? td.CashIn ?? td.cashin ?? 0) || 0
  const cashDrop = Number(td.CashDrop_alloc ?? td.cashdrop_alloc ?? td.CashDrop ?? td.cashdrop ?? 0) || 0
  // Session CashIn is often already dollars; alloc fields are usually meter cents. Prefer _alloc when present.
  const hasAlloc = td.CashIn_alloc != null || td.cashin_alloc != null || td.CashDrop_alloc != null || td.cashdrop_alloc != null
  const row = {
    SessionPK: String(spk),
    MemberID: String(td.MemberID ?? td.memberid ?? alertRow?.member_id ?? '—'),
    EgmPK: td.EgmPK ?? td.egmpk ?? '—',
    SessionStart: td.SessionStart ?? td.StartTime ?? td.starttime ?? null,
    SessionEnd: td.SessionEnd ?? td.EndTime ?? td.endtime ?? null,
    IntervalCount: td.IntervalCount ?? 1,
    Turnover_alloc: td.Turnover_alloc ?? td.turnover_alloc ?? td.Turnover ?? td.turnover,
    CreditWin_alloc: td.CreditWin_alloc ?? td.creditwin_alloc ?? td.CreditWin ?? td.creditwin,
    CashIn_alloc: hasAlloc ? cashIn : null,
    CashDrop_alloc: hasAlloc ? cashDrop : null,
    /** When only session dollars exist, stash for display without treating as cents */
    CashIn_dollars: !hasAlloc && (cashIn || cashDrop) ? cashIn || cashDrop : null,
    CashlessIn_alloc: td.CashlessIn_alloc ?? td.cashlessin_alloc ?? td.CashlessIn ?? td.cashlessin,
    Stroke_alloc: td.Stroke_alloc ?? td.stroke_alloc ?? td.Stroke ?? td.stroke,
    Venue: td.Venue ?? td.venue ?? null,
    IsUncarded: false,
  }

  return {
    rows: [row],
    hint:
      'No combined attribution rows in this ±6h window (Process & Combine may not have run). Showing the alert evaluation record.',
  }
}

export function findTriggerCombinedRowIndex(rows, detailsParsed, alertRow) {
  if (!Array.isArray(rows) || rows.length === 0) return null
  const target = resolveAlertSessionPk(alertRow, detailsParsed)
  if (!target) return null
  const i = rows.findIndex((r) => String(r.SessionPK ?? r.sessionpk ?? '') === target)
  if (i < 0) return null
  return { index: i, matchKind: 'alert session', badge: 'Trigger', sessionPk: target }
}

/**
 * When GET /api/gaming/sessions returns no rows, derive session-shaped rows from alert payload.
 */
export function gamingModalSessionsFallbackRows(detailsParsed, alertRow, fromStr, toStr) {
  const member = String(alertRow?.member_id || '').trim()
  const fromMs = parseAlertTimeMs(fromStr)
  const toMs = parseAlertTimeMs(toStr)
  const windowOk = !Number.isNaN(fromMs) && !Number.isNaN(toMs) && toMs >= fromMs

  const inWindow = (iso) => {
    if (!windowOk) return true
    const t = parseAlertTimeMs(iso)
    if (Number.isNaN(t)) return false
    return t >= fromMs && t <= toMs
  }

  const mapSessionRow = (data, startFallback) => {
    const spk = data.SessionPK ?? data.sessionpk
    if (!spk) return null
    const st = data.StartTime ?? data.starttime ?? startFallback ?? data.SnapshotTime ?? data.SnapShotTime
    const et = data.EndTime ?? data.endtime ?? startFallback ?? data.SnapshotTime ?? data.SnapShotTime
    return {
      SessionPK: spk,
      MemberID: String((data.MemberID ?? data.memberid ?? member) || '—'),
      EgmPK: data.EgmPK ?? data.EgmHouseNo ?? data.egmpk ?? '—',
      StartTime: st,
      EndTime: et,
      Turnover: data.Turnover ?? data.turnover,
      CreditWin: data.CreditWin ?? data.creditwin,
      Venue: data.Venue ?? data.venue,
    }
  }

  const out = []
  const seen = new Set()
  const pushRow = (data, startFb) => {
    const row = mapSessionRow(data, startFb)
    if (!row || seen.has(row.SessionPK)) return
    seen.add(row.SessionPK)
    out.push(row)
  }

  const td = detailsParsed?.triggered_data
  if (td && typeof td === 'object' && !Array.isArray(td) && td.SessionPK) {
    const anchor = td.StartTime ?? td.EndTime ?? td.SnapshotTime ?? td.SnapShotTime
    const mid = String(td.MemberID ?? td.memberid ?? '')
    if (!(member && mid && mid !== member) && anchor && inWindow(anchor)) {
      pushRow(td, null)
    }
  }

  for (const { data } of extractCumulativeMeterSnapshots(detailsParsed)) {
    if (!data?.SessionPK && !data?.sessionpk) continue
    const st = data.SnapshotTime ?? data.SnapShotTime
    const mid = String(data.MemberID ?? data.memberid ?? '')
    if (member && mid && mid !== member) continue
    if (st && inWindow(st)) pushRow(data, st)
  }

  let hint = null
  if (out.length > 0) {
    hint =
      'No rows from the tenant gaming DB for this ±6h window. Showing session-linked rows from the alert that fall inside the window.'
  } else if (td && typeof td === 'object' && !Array.isArray(td) && td.SessionPK) {
    const anchor = td.StartTime ?? td.EndTime ?? td.SnapshotTime ?? td.SnapShotTime
    if (anchor) {
      pushRow(td, null)
      hint =
        'No tenant DB sessions in ±6h. Showing session context from the alert payload (start/end may mirror the meter snapshot when the feed did not store session times).'
    }
  }
  if (out.length === 0) {
    for (const { data } of extractCumulativeMeterSnapshots(detailsParsed)) {
      if (!data?.SessionPK && !data?.sessionpk) continue
      const mid = String(data.MemberID ?? data.memberid ?? '')
      if (member && mid && mid !== member) continue
      const st = data.SnapshotTime ?? data.SnapShotTime
      pushRow(data, st)
    }
    if (out.length) {
      hint =
        'No tenant DB sessions in ±6h. Showing session IDs from cumulative evidence on the alert (timestamps are snapshot times).'
    }
  }

  return { rows: out, hint }
}

export const GAMING_DATA_TABS = new Set(['import', 'meter', 'sessions', 'combined', 'summary', 'rules-test'])

export function readGamingDataSearchParams(searchParams) {
  const tabRaw = searchParams.get('tab')
  return {
    tab: GAMING_DATA_TABS.has(tabRaw) ? tabRaw : null,
    egm_pk: searchParams.get('egm_pk') || searchParams.get('egm') || '',
    egmpks: searchParams.get('egmpks') || '',
    member_id: searchParams.get('member_id') || '',
    from: toDatetimeLocalFromAny(searchParams.get('from') || ''),
    to: toDatetimeLocalFromAny(searchParams.get('to') || ''),
    trading_day: searchParams.get('trading_day') || '',
    view: searchParams.get('view') || '',
    hours: searchParams.get('hours') || '',
    machine_class: searchParams.get('machine_class') || '',
    alert_id: searchParams.get('alert_id') || '',
  }
}

export function buildGamingDataUrl({
  tab = 'combined',
  egm_pk,
  egmpks,
  member_id,
  from,
  to,
  trading_day,
  view,
  hours,
  machine_class,
  alertId,
}) {
  const params = new URLSearchParams()
  if (tab) params.set('tab', tab)
  if (egm_pk) params.set('egm_pk', String(egm_pk))
  if (egmpks) params.set('egmpks', String(egmpks))
  if (member_id) params.set('member_id', String(member_id))
  // Gaming Data uses datetime-local inputs; convert absolute ISO to local wall clock.
  const fromLocal = toDatetimeLocalFromAny(from)
  const toLocal = toDatetimeLocalFromAny(to)
  if (fromLocal) params.set('from', fromLocal)
  if (toLocal) params.set('to', toLocal)
  if (trading_day) params.set('trading_day', String(trading_day).slice(0, 10))
  if (view) params.set('view', String(view))
  if (hours) params.set('hours', String(hours))
  if (machine_class && machine_class !== 'all') params.set('machine_class', String(machine_class))
  if (alertId != null && alertId !== '') params.set('alert_id', String(alertId))
  const qs = params.toString()
  return `/monitor-safe/gaming-data${qs ? `?${qs}` : ''}`
}

/** Flatten nested and/or condition trees into leaf nodes. */
export function flattenRuleConditions(conditions, join = 'and') {
  if (!conditions) return []
  if (Array.isArray(conditions)) {
    return conditions.flatMap((c) => flattenRuleConditions(c, join))
  }
  if (typeof conditions !== 'object') return []
  if (conditions.type === 'and' || conditions.type === 'or') {
    const kids = Array.isArray(conditions.conditions) ? conditions.conditions : []
    return kids.flatMap((c) => flattenRuleConditions(c, conditions.type))
  }
  return [{ ...conditions, _join: join }]
}

function readTriggerField(data, field) {
  if (!data || field == null) return undefined
  if (Object.prototype.hasOwnProperty.call(data, field)) return data[field]
  const lower = String(field).toLowerCase()
  for (const [k, v] of Object.entries(data)) {
    if (String(k).toLowerCase() === lower) return v
  }
  return undefined
}

/** Session / enriched sits store cash in AUD; only legacy meter-style cash needs /100. */
function triggeredCashIsAud(triggeredData) {
  if (!triggeredData || typeof triggeredData !== 'object') return false
  const unit = String(triggeredData.cash_fields_unit || triggeredData._cash_fields_unit || '').toUpperCase()
  if (unit === 'AUD') return true
  if (unit === 'CENTS' || unit === 'METER_CENTS') return false
  // Live-run / session payloads: SessionPK present ⇒ dollars (same as dry-run enrichedRowToPayload).
  if (triggeredData.SessionPK != null || triggeredData.sessionpk != null) return true
  if (triggeredData.StartTime != null || triggeredData.starttime != null) return true
  return false
}

/** Convert stored CashIn/CashDrop/Cashless* to AUD for compare. */
function cashFieldAsAud(field, value, triggeredData = null) {
  const n = Number(value)
  if (!Number.isFinite(n)) return n
  const lk = String(field || '').toLowerCase()
  if (!/^(cashin|cashdrop|cashlessin|cashlessout)$/.test(lk)) return n
  if (triggeredCashIsAud(triggeredData)) return n
  // Legacy payloads stored meter cents as the cash field (e.g. 1265.67 → $12.66).
  return n / 100
}

/** Rule expression thresholds are authored in AUD dollars — never treat as meter cents. */
function formatThresholdValue(field, value) {
  if (value == null || value === '') return '—'
  const n = Number(value)
  const moneyish = /cash|turnover|win|drop|amount|bet/i.test(String(field || ''))
  if (moneyish && Number.isFinite(n)) return formatGamingMoney(n)
  if (Number.isFinite(n)) return n.toLocaleString(undefined, { maximumFractionDigits: 4 })
  return String(value)
}

function formatConditionValue(field, value, triggeredData = null) {
  if (value == null || value === '') return '—'
  const moneyish = /cash|turnover|win|drop|amount|bet/i.test(String(field || ''))
  const n = Number(value)
  if (moneyish && Number.isFinite(n)) {
    const lk = String(field || '').toLowerCase()
    if (/^(cashin|cashdrop|cashlessin|cashlessout)$/.test(lk) || (/_alloc$/.test(lk) && /cash/i.test(lk))) {
      // Prefer AUD formatting when session/live unit is known.
      if (triggeredCashIsAud(triggeredData)) return formatGamingMoney(n)
      return formatTriggerFieldValue(field, value, triggeredData)
    }
    return formatGamingMoney(n)
  }
  if (Number.isFinite(n) && String(value).trim() !== '' && !Number.isNaN(n)) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 4 })
  }
  return String(value)
}

function evalCompare(left, op, right) {
  const a = Number(left)
  const b = Number(right)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  switch (op) {
    case '>=': return a >= b
    case '<=': return a <= b
    case '>': return a > b
    case '<': return a < b
    case '==':
    case '=': return a === b
    case '!=': return a !== b
    default: return null
  }
}

/**
 * Build a plain checklist explaining why an alert matched the published rule.
 * Supports common expression forms: `${Field} >= 300`, `(${A} / ${B}) >= 3`.
 *
 * @returns {{ summary: string, rows: object[], join: string } | null}
 */
export function buildWhyThisFired(ruleDefinition, triggeredData) {
  const data = triggeredData && typeof triggeredData === 'object' ? triggeredData : null
  const leaves = flattenRuleConditions(ruleDefinition?.conditions)
  if (!leaves.length) {
    if (ruleDefinition?.description) {
      return {
        summary: String(ruleDefinition.description),
        rows: [],
        join: 'and',
      }
    }
    return null
  }

  const join = leaves[0]?._join === 'or' ? 'or' : 'and'
  const rows = []

  for (const leaf of leaves) {
    const expr = String(leaf.expression || leaf.expr || '').trim()
    let label = expr || leaf.field || 'Condition'
    let actualDisplay = '—'
    let expectedDisplay = ''
    let passed = null
    let fieldHint = null

    // ${Field} op number
    let m = expr.match(/^\$\{([A-Za-z0-9_]+)\}\s*(>=|<=|==|!=|>|<|=)\s*(-?\d+(?:\.\d+)?)\s*$/)
    if (m) {
      const field = m[1]
      const op = m[2]
      const threshold = Number(m[3])
      const rawActual = data ? readTriggerField(data, field) : undefined
      const actual = data != null && rawActual != null ? cashFieldAsAud(field, rawActual, data) : rawActual
      fieldHint = field
      // Thresholds are authored in AUD (e.g. CashIn >= 300 means $300), not meter cents.
      label = `${field} ${op} ${formatThresholdValue(field, threshold)}`
      actualDisplay = formatConditionValue(field, rawActual, data)
      expectedDisplay = `${op} ${formatThresholdValue(field, threshold)}`
      // Compare actual AUD to threshold AUD (same units the engine uses for session rules).
      passed = data ? evalCompare(actual, op, threshold) : null
    } else {
      // (${A} / ${B}) op number
      m = expr.match(
        /^\(\s*\$\{([A-Za-z0-9_]+)\}\s*\/\s*\$\{([A-Za-z0-9_]+)\}\s*\)\s*(>=|<=|==|!=|>|<|=)\s*(-?\d+(?:\.\d+)?)\s*$/
      )
      if (m) {
        const aField = m[1]
        const bField = m[2]
        const op = m[3]
        const threshold = Number(m[4])
        const aRaw = data ? Number(readTriggerField(data, aField)) : NaN
        const bRaw = data ? Number(readTriggerField(data, bField)) : NaN
        const aVal = data != null && Number.isFinite(aRaw) ? cashFieldAsAud(aField, aRaw, data) : aRaw
        const bVal = data != null && Number.isFinite(bRaw) ? cashFieldAsAud(bField, bRaw, data) : bRaw
        const ratio = Number.isFinite(aVal) && Number.isFinite(bVal) && bVal !== 0 ? aVal / bVal : null
        fieldHint = `${aField}/${bField}`
        label = `${aField} / ${bField} ${op} ${threshold}`
        actualDisplay = ratio == null ? '—' : `${ratio.toLocaleString(undefined, { maximumFractionDigits: 2 })}×`
        expectedDisplay = `${op} ${threshold}`
        passed = ratio == null ? null : evalCompare(ratio, op, threshold)
      } else if (leaf.field && (leaf.operator || leaf.op) && leaf.value != null) {
        const field = leaf.field
        const op = leaf.operator || leaf.op
        const threshold = leaf.value
        const rawActual = data ? readTriggerField(data, field) : undefined
        const actual = data != null && rawActual != null ? cashFieldAsAud(field, rawActual, data) : rawActual
        fieldHint = field
        label = `${field} ${op} ${formatThresholdValue(field, threshold)}`
        actualDisplay = formatConditionValue(field, rawActual, data)
        expectedDisplay = `${op} ${formatThresholdValue(field, threshold)}`
        passed = data ? evalCompare(actual, op, threshold) : null
      } else if (expr) {
        label = expr.replace(/\$\{([A-Za-z0-9_]+)\}/g, '$1')
      }
    }

    rows.push({
      label,
      expression: expr || null,
      field: fieldHint,
      actual: actualDisplay,
      expected: expectedDisplay,
      passed,
      join,
    })
  }

  const cashIn = data ? readTriggerField(data, 'CashIn') : null
  const turnover = data ? readTriggerField(data, 'Turnover') : null
  let summary = ruleDefinition?.description || ruleDefinition?.name || 'Rule matched'
  if (cashIn != null && turnover != null) {
    summary = `Physical cash ${formatConditionValue('CashIn', cashIn, data)} vs turnover ${formatConditionValue('Turnover', turnover, data)} on this sit`
  } else if (ruleDefinition?.name) {
    summary = ruleDefinition.name
  }

  return { summary, rows, join }
}

/**
 * Transaction ids on alerts can be an ISO instant (e.g. "2026-08-22T08:15:00.533Z").
 * Show those as Sydney date-time; leave real ids (numbers / codes) untouched.
 */
export function formatTransactionRef(value) {
  if (value == null || value === '') return '—'
  const raw = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return formatSydneyDateTimeSec(raw)
  return raw
}
